import "./lib/safeStorage";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { initSync } from "./lib/sync";
import { ErrorBoundary, recordClientError, flushHeldCrashReports } from "./components/ErrorBoundary";
import { i18nReady } from "./lib/i18n";
import "./index.css";

initSync();

/* Pages now load on demand. After a deploy, a tab still running the old build
   asks for page files that no longer exist — reload once onto the new build
   instead of crashing. The session flag stops a reload loop if the network,
   not the deploy, is the problem. */
if (typeof window !== "undefined") {
  const RELOAD_KEY = "atelier.chunkReloadAt";
  const reloadForNewBuild = () => {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last < 30_000) return false;
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch { /* storage blocked — reload anyway, once per page life */ }
    location.reload();
    return true;
  };
  /* A failed chunk can be worse than missing: if a deploy window ever served
     the SPA page under the chunk's /assets/ url, the device's HTTP cache holds
     that HTML as "immutable" — and a plain reload re-imports the same poison
     forever (seen in the field: the same crash six times in a row on one
     device). So before reloading, the failing url is refetched with
     cache:"reload", which overwrites the poisoned entry with whatever the
     origin serves now. */
  const CHUNK_FAIL = /dynamically imported module|Importing a module script failed|error loading dynamically imported|stale page module/i;
  const recoverFromStaleChunk = (msg: string): boolean => {
    try {
      if (Date.now() - Number(sessionStorage.getItem(RELOAD_KEY) || 0) < 30_000) return false;
    } catch { /* storage blocked — carry on */ }
    const url = msg.match(/https?:\/\/\S+\/assets\/\S+?\.(?:js|css)/)?.[0];
    const bust = url ? fetch(url, { cache: "reload" }).catch(() => undefined) : Promise.resolve(undefined);
    void bust.finally(() => { if (!reloadForNewBuild()) location.reload(); });
    return true;
  };
  window.addEventListener("vite:preloadError", (e) => {
    const msg = String((e as unknown as { payload?: { message?: string } }).payload?.message ?? "");
    if (recoverFromStaleChunk(msg)) e.preventDefault();
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = String((e.reason as { message?: string })?.message ?? e.reason ?? "");
    if (CHUNK_FAIL.test(msg) && recoverFromStaleChunk(msg)) e.preventDefault();
  });
  (window as unknown as { __reloadForNewBuild?: () => boolean }).__reloadForNewBuild = reloadForNewBuild;
}

/* A picture dropped anywhere but on a drop zone makes the browser open the
   file in place of the app — on a Mac, dragging from Photos or Finder and
   missing the dashed box by a few pixels looked like "the page reloaded and
   my photos were gone". Outside a zone a drop now does nothing at all; the
   zones still handle their own. */
if (typeof window !== "undefined") {
  window.addEventListener("dragover", (e) => { if (e.dataTransfer?.types.includes("Files") || e.dataTransfer?.types.includes("text/uri-list")) e.preventDefault(); });
  window.addEventListener("drop", (e) => { if (!e.defaultPrevented) e.preventDefault(); });
}

/* Browser translation (Edge / Chrome "translate this page") rewrites the
   text nodes React owns, wrapping them in <font> tags. The next time React
   updates that text it tries to remove a node that is no longer there and the
   whole page dies with "removeChild … is not a child of this node". The
   Chinese site crews translate every page, so the two DOM calls that fail
   are made forgiving: a node that is already gone is simply left alone. */
if (typeof Node !== "undefined") {
  const proto = Node.prototype;
  const origRemove = proto.removeChild;
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      if (child.parentNode) child.parentNode.removeChild(child);
      return child;
    }
    return origRemove.call(this, child) as T;
  };
  const origInsert = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(this: Node, node: T, ref: Node | null): T {
    if (ref && ref.parentNode !== this) return (this.appendChild(node) as T);
    return origInsert.call(this, node, ref) as T;
  };
}

/* Errors outside React's render — a failed upload handler, a rejected
   promise — don't blank the page, but they used to vanish without trace.
   They are kept on the device and reported to the studio. */
window.addEventListener("error", (e) => recordClientError(String(e.message || e.error || "Script error"), e.error?.stack));
// reports held while the device was offline go out once it is back
setTimeout(flushHeldCrashReports, 8000);
window.addEventListener("online", flushHeldCrashReports);

window.addEventListener("unhandledrejection", (e) => {
  const r = e.reason as { message?: string; stack?: string } | string | undefined;
  recordClientError(`Unhandled: ${typeof r === "string" ? r : r?.message ?? String(r)}`, typeof r === "object" ? r?.stack : undefined);
});

// Installable app: register the service worker in production builds only.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => { /* non-fatal */ });
  });
}

/* Every file picker in the app is a hidden <input type=file> behind a
   <label>. Some browsers — embedded webviews, older desktop builds — never
   open the picker from a label click, and a camera-only input is dead on a
   laptop. So every such click becomes a direct click on the input, inside
   the user's gesture, with the camera hint dropped on devices that have no
   touch screen (and so no rear camera to offer). */
document.addEventListener("click", (e) => {
  const el = e.target as Element | null;
  const label = el?.closest?.("label[for]") as HTMLLabelElement | null;
  if (!label) return;
  const input = document.getElementById(label.htmlFor) as HTMLInputElement | null;
  if (!input || input.type !== "file" || input.disabled) return;
  e.preventDefault();
  const coarse = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  if (!coarse && input.hasAttribute("capture")) input.removeAttribute("capture");
  input.click();
}, true);

// a Chinese-language session paints once its dictionary is in (English: at once)
void i18nReady().then(() => createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
));


